import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { HiOutlineCheck } from "react-icons/hi";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import pdfIcon from "assets/svg/pdf-icon.svg";

import {
  getLegalCertificateNoticeToClientPreview,
  getLegalCertificateProgress,
  LegalCertificateWorkflowStep,
  sendLegalCertificateNoticeToClient,
  type LegalCertificateNoticeToClientApplicationGroup,
  type LegalCertificateNoticeToClientDocument,
  type LegalCertificateNoticeToClientPreview,
} from "../../api/legalCertificate";

export const LEGAL_CERTIFICATE_NOTICE_TO_CLIENT_FORM_ID =
  "legal-certificate-notice-to-client-form";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function LegalCertificateNoticeToClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [preview, setPreview] =
    useState<LegalCertificateNoticeToClientPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setHasSent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getLegalCertificateProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === LegalCertificateWorkflowStep.LC_S3_04_NoticeToClient
        );
        if (!cancelled && step) setHasSent(step.isCompleted);
      } catch {
        if (!cancelled) setHasSent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getLegalCertificateNoticeToClientPreview(leadId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(
          err instanceof Error ? err.message : "Failed to load preview"
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const documents = preview?.documents ?? [];
  const recipientPhone = preview?.recipientPhone ?? "";
  const recipientEmail = preview?.recipientEmail ?? "";
  const applicationNumbersFromPreview = preview?.applicationNumbers ?? "";
  const coveringNote = preview?.coveringNote ?? null;

  const applicationGroups = useMemo<
    LegalCertificateNoticeToClientApplicationGroup[]
  >(() => {
    if (preview?.applications?.length) return preview.applications;
    if (!applicationNumbersFromPreview) return [];
    return applicationNumbersFromPreview
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map<LegalCertificateNoticeToClientApplicationGroup>((tmNo, idx) => ({
        applicationId: `${tmNo}-${idx}`,
        tmNo,
        mark: null,
        legalCertificate: null,
        registrationCertificate: null,
      }));
  }, [preview?.applications, applicationNumbersFromPreview]);

  const canSend =
    !previewError && documents.length > 0 && !!recipientPhone;

  const triggerSend = (mode: "send" | "resend") => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        previewError ??
          "Upload the Legal Certificate, Registration Certificate, or generate the Covering Note before sending."
      );
      return;
    }
    setConfirmMode(mode);
    setShowConfirmModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      onSuccess?.(leadId);
      return;
    }
    triggerSend("send");
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        await sendLegalCertificateNoticeToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: !!recipientEmail,
        });
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? "Notice resent to client successfully."
            : "Notice sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  const previewBlocks = documents.map(
    (doc) =>
      `${doc.label} — ${doc.filename}\nApplication No. ${applicationNumbersFromPreview || "—"}`
  );

  const hasAnyApplicationDocs = applicationGroups.some(
    (g) => g.legalCertificate || g.registrationCertificate
  );

  return (
    <form
      id={LEGAL_CERTIFICATE_NOTICE_TO_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Intro */}
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0.01em",
          }}
        >
          Forward Legal Certificate to Client
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            letterSpacing: "0.04em",
          }}
        >
          The Covering Note, Registration Certificate and Legal Certificate
          PDFs will be forwarded to the client's WhatsApp
          {recipientEmail ? " and Email" : ""} for each application below.
        </p>
      </div>

      {/* Covering Note (lead-level, forwarded once) */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-normal text-[#222]" style={LABEL_FEATURES}>
          Covering Note
        </p>
        {previewLoading && !coveringNote ? (
          <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px]">
            <p
              className="text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={LABEL_FEATURES}
            >
              Loading…
            </p>
          </div>
        ) : coveringNote ? (
          <DocumentRow doc={coveringNote} />
        ) : (
          <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px]">
            <p
              className="text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={LABEL_FEATURES}
            >
              {previewError ??
                "Covering Note not generated yet. Complete Step 2 (Prepare Kit) before sending."}
            </p>
          </div>
        )}
      </div>

      {/* Applications — one card per TM No. with its Registration & Legal Certificate PDFs */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-normal text-[#222]" style={LABEL_FEATURES}>
          Application(s) ({applicationGroups.length || 0})
        </p>
        {previewLoading && applicationGroups.length === 0 ? (
          <p className="text-xs text-[#6D6D6D]" style={LABEL_FEATURES}>
            Loading applications…
          </p>
        ) : applicationGroups.length === 0 ? (
          <p className="text-xs text-[#6D6D6D]" style={LABEL_FEATURES}>
            {previewError ??
              "No application numbers found. Complete Application Details first."}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {applicationGroups.map((app, idx) => (
              <div
                key={app.applicationId ?? `${app.tmNo ?? "app"}-${idx}`}
                className="flex flex-col gap-2 p-3 bg-white border border-[#EEEEEE] rounded-[8px]"
              >
                <div className="flex flex-row items-center gap-2.5 flex-wrap">
                  <span
                    className="text-[12px] font-medium leading-5 text-[#222222]"
                    style={LABEL_FEATURES}
                  >
                    TM No. {app.tmNo ?? "—"}
                  </span>
                  {app.mark ? (
                    <span
                      className="text-[12px] font-normal leading-5 text-[#6D6D6D] truncate"
                      style={LABEL_FEATURES}
                      title={app.mark}
                    >
                      · {app.mark}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  {app.legalCertificate ? (
                    <DocumentRow doc={app.legalCertificate} />
                  ) : (
                    <MissingDocumentRow label="Legal Certificate not uploaded yet" />
                  )}
                  {app.registrationCertificate ? (
                    <DocumentRow doc={app.registrationCertificate} />
                  ) : (
                    <MissingDocumentRow label="Registration Certificate not uploaded yet" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!previewLoading && !hasAnyApplicationDocs && !previewError ? (
          <p className="text-[11px] text-[#B45309]" style={LABEL_FEATURES}>
            Upload the Legal Certificate and Registration Certificate for each
            application in Step 1 before sending.
          </p>
        ) : null}
      </div>

      {/* Recipient */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-normal text-[#222]" style={LABEL_FEATURES}>
          Recipient
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={recipientPhone}
            readOnly
            placeholder="e.g. +919876543210"
            className="w-full min-w-0 sm:w-[220px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          {recipientEmail ? (
            <input
              type="email"
              value={recipientEmail}
              readOnly
              className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
            />
          ) : null}
          {hasSent && (
            <button
              type="button"
              disabled={sending || !canSend}
              onClick={() => triggerSend("resend")}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Resend Message"}
            </button>
          )}
        </div>
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewBlocks.length > 0 ? previewBlocks : "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
        title={
          confirmMode === "resend"
            ? "Confirm Resend Notice to Client"
            : "Confirm Notice to Client"
        }
      />
    </form>
  );
}

function DocumentRow({
  doc,
}: {
  doc: LegalCertificateNoticeToClientDocument;
}) {
  return (
    <div className="flex flex-row items-center gap-3 px-3 py-2.5 bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px]">
      <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span
          className="text-[12px] font-medium leading-5 text-[#222]"
          style={LABEL_FEATURES}
        >
          {doc.label}
        </span>
        <span
          className="text-[11px] leading-4 text-[#6D6D6D] truncate"
          style={LABEL_FEATURES}
          title={doc.filename}
        >
          {doc.filename}
        </span>
      </div>
      <span
        className="flex items-center gap-1 text-[11px] font-medium text-[#16A34A] flex-shrink-0"
        style={LABEL_FEATURES}
      >
        <HiOutlineCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
        Ready
      </span>
    </div>
  );
}

function MissingDocumentRow({ label }: { label: string }) {
  return (
    <div className="flex flex-row items-center gap-3 px-3 py-2.5 bg-white border border-dashed border-[#EEEEEE] rounded-[6px]">
      <img
        src={pdfIcon}
        alt=""
        className="h-[22px] w-[22px] shrink-0 opacity-40"
      />
      <span
        className="text-[12px] leading-5 text-[#B45309] flex-1"
        style={LABEL_FEATURES}
      >
        {label}
      </span>
    </div>
  );
}
